import React from "react";
import clsx from "clsx";
import {
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteVideoTrack,
} from "twilio-video";
import { makeStyles } from "@mui/styles";
import { Typography, Tooltip } from "@mui/material";
import useIsTrackSwitchedOff from "../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff";
import useParticipantIsReconnecting from "../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting";
import usePublications from "../../hooks/usePublications/usePublications";
import useTrack from "../../hooks/useTrack/useTrack";
import AvatarIcon from "../../icons/AvatarIcon";
import { participantNameExtractor } from "../../utils/common-functions";
import AudioLevelIndicator from "../AudioLevelIndicator/AudioLevelIndicator";

const useStyles = makeStyles(() => ({
  container: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  identity: {
    background: "rgba(0, 0, 0, 0.5)",
    color: "white",
    height: "40px",
    padding: "0.5em 0.3em 0 0",
    display: "inline-flex",
    "& svg": {
      marginLeft: "0.3em",
    },
    marginBottom: 0,
    marginTop: "30px",
    marginRight: "0.4em",
  },
  infoContainer: {
    position: "absolute",
    zIndex: 2,
    height: "100%",
    width: "100%",
  },
  reconnectingContainer: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(40, 42, 43, 0.75)",
    zIndex: 1,
  },
  fullWidth: {
    gridArea: "1 / 1 / 2 / 3",
  },
  avatarContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "black",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    "& svg": {
      transform: "scale(2)",
    },
  },
  recordingIndicator: {
    position: "absolute",
    bottom: 10,
    display: "flex",
    alignItems: "center",
    background: "rgba(0, 0, 0, 0.5)",
    color: "white",
    padding: "0.1em 0.3em 0.1em 0",
    fontSize: "1.2rem",
    height: "28px",
  },
  recordingText: {
    marginTop: "15px",
  },
  circle: {
    height: "12px",
    width: "12px",
    background: "red",
    borderRadius: "100%",
    margin: "0 0.6em",
    animation: `1.25s $pulsate ease-out infinite`,
  },
  "@keyframes pulsate": {
    "0%": {
      background: `#A90000`,
    },
    "50%": {
      background: "#f00",
    },
    "100%": {
      background: "#A90000",
    },
  },
}));

interface IMainParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  room?: any;
}

const MainParticipantInfo: React.FC<IMainParticipantInfoProps> = ({
  participant,
  children,
  room,
}) => {
  const classes = useStyles();
  //   const { room } = window;
  const localParticipant = room?.localParticipant;
  const isLocal = localParticipant === participant;

  const publications = usePublications(participant);
  const videoPublication = publications?.find(
    (p) => !p.trackName.includes("screen") && p.kind === "video"
  );
  const screenSharePublication = publications?.find((p) =>
    p.trackName.includes("screen")
  );

  const videoTrack = useTrack(videoPublication);
  const isVideoEnabled = Boolean(videoTrack);

  const audioPublication = publications?.find((p) => p.kind === "audio");
  const audioTrack = useTrack(audioPublication) as
    | LocalAudioTrack
    | RemoteAudioTrack
    | undefined;

  const isVideoSwitchedOff = useIsTrackSwitchedOff(
    videoTrack as LocalVideoTrack | RemoteVideoTrack
  );
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  return (
    <div
      data-cy-main-participant
      data-cy-participant={participant?.identity}
      className={clsx(classes.container)}
    >
      <div className={classes.infoContainer}>
        <div style={{ display: "flex" }}>
          <div className={classes.identity}>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography variant="body1" color="inherit">
              {participantNameExtractor(participant?.identity)}
              {isLocal && " (You)"}
              {screenSharePublication && " - Screen"}
            </Typography>
          </div>
        </div>
      </div>
      {(!isVideoEnabled || isVideoSwitchedOff) && (
        <div className={classes.avatarContainer}>
          <AvatarIcon />
        </div>
      )}
      {isParticipantReconnecting && (
        <div className={classes.reconnectingContainer}>
          <Typography variant="body1" style={{ color: "white" }}>
            Reconnecting...
          </Typography>
        </div>
      )}
      {children}
    </div>
  );
};

export default MainParticipantInfo;
